import React, { useState } from "react";
import {
    Box,
    Dialog,
    Stack,
    Button,
    Text,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHeaderCell,
    TableRow,
    Actions,
    Alert,
    TextField,
    Heading,
    IconMoney,
    Dropdown,
} from "braid-design-system";

interface ChequeEntry {
    id: string;
    description: string;
    receiptNo: string;
    date: string;
    amount: number;
    currentAcc: string;
}

interface SendChequesToBankDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

const SendChequesToBankDialog: React.FC<SendChequesToBankDialogProps> = ({
    isOpen,
    onClose,
}) => {
    const [cheques, setCheques] = useState<ChequeEntry[]>([
        {
            id: "1",
            description: "",
            receiptNo: "",
            date: "",
            amount: 0,
            currentAcc: "Select C/A",
        },
    ]);

    const handleAddRow = () => {
        const newCheque: ChequeEntry = {
            id: String(cheques.length + 1),
            description: "",
            receiptNo: "",
            date: "",
            amount: 0,
            currentAcc: "Select C/A",
        };
        setCheques([...cheques, newCheque]);
    };

    const handleRemoveRow = (id: string) => {
        if (cheques.length > 1) {
            setCheques(cheques.filter((cheque) => cheque.id !== id));
        }
    };

    const handleChequeChange = (
        id: string,
        field: keyof ChequeEntry,
        value: any
    ) => {
        setCheques(
            cheques.map((cheque) =>
                cheque.id === id ? { ...cheque, [field]: value } : cheque
            )
        );
    };

    const calculateTotal = (): number => {
        return cheques.reduce((sum, cheque) => sum + cheque.amount, 0);
    };

    const handleSubmit = () => {
        // TODO: Add API call to submit cheques
        onClose();
    };

    return (
        <Box>
            {isOpen && (
                <Dialog
                    id="send-cheques-dialog"
                    title="Send Cheques to Bank"
                    open={true}
                    onClose={onClose}
                    width="large"
                >
                    <Stack space="large">
                        <Alert tone="info">
                            <Text>
                                Add cheques that need to be sent to the bank for deposit.
                            </Text>
                        </Alert>

                        {/* Cheques Table */}
                        <Stack space="small">
                            <Heading level="4">Cheque Details</Heading>
                            <Box overflow="auto">
                                <Table label="Cheques Table">
                                    <TableBody>
                                        {cheques.map((cheque) => (
                                            <TableRow key={cheque.id}>
                                                <TableCell>
                                                    <TextField
                                                        label="Description"
                                                        value={cheque.description}
                                                        onChange={(e) =>
                                                            handleChequeChange(
                                                                cheque.id,
                                                                "description",
                                                                e.currentTarget.value
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        label="Receipt No"
                                                        value={cheque.receiptNo}
                                                        onChange={(e) =>
                                                            handleChequeChange(
                                                                cheque.id,
                                                                "receiptNo",
                                                                e.currentTarget.value
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        label="Date"
                                                        value={cheque.date}
                                                        onChange={(e) =>
                                                            handleChequeChange(
                                                                cheque.id,
                                                                "date",
                                                                e.currentTarget.value
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <TextField
                                                        label="Amount"
                                                        type="number"
                                                        inputMode="decimal"
                                                        value={
                                                            cheque.amount > 0
                                                                ? cheque.amount.toString()
                                                                : ""
                                                        }
                                                        onChange={(e) =>
                                                            handleChequeChange(
                                                                cheque.id,
                                                                "amount",
                                                                parseFloat(
                                                                    e.currentTarget.value
                                                                ) || 0
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Dropdown
                                                        label="Account"
                                                        value={cheque.currentAcc}
                                                        onChange={(e) =>
                                                            handleChequeChange(
                                                                cheque.id,
                                                                "currentAcc",
                                                                e.currentTarget.value
                                                            )
                                                        }
                                                    >
                                                        <option value="">Select Account</option>
                                                        <option value="C/A 001">C/A 001</option>
                                                        <option value="C/A 002">C/A 002</option>
                                                        <option value="C/A 003">C/A 003</option>
                                                        <option value="C/A 004">C/A 004</option>
                                                        <option value="C/A 005">C/A 005</option>
                                                    </Dropdown>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        variant="transparent"
                                                        onClick={() =>
                                                            handleRemoveRow(cheque.id)
                                                        }
                                                    >
                                                        {cheques.length > 1 ? "Remove" : ""}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow>
                                            <TableHeaderCell>
                                                <Text weight="strong">Total:</Text>
                                            </TableHeaderCell>
                                            <TableHeaderCell>
                                                <Text />
                                            </TableHeaderCell>
                                            <TableHeaderCell>
                                                <Text />
                                            </TableHeaderCell>
                                            <TableHeaderCell align="right">
                                                <Text
                                                    weight="strong"
                                                    icon={<IconMoney />}
                                                    tone="positive"
                                                >
                                                    {new Intl.NumberFormat("en-US", {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    }).format(calculateTotal())}
                                                </Text>
                                            </TableHeaderCell>
                                            <TableHeaderCell>
                                                <Text />
                                            </TableHeaderCell>
                                            <TableHeaderCell>
                                                <Text />
                                            </TableHeaderCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </Box>
                        </Stack>

                        {/* Add Row Button */}
                        <Box display="flex" justifyContent="flexStart">
                            <Button onClick={handleAddRow}>
                                Add Row
                            </Button>
                        </Box>

                        {/* Dialog Actions */}
                        <Box display="flex" justifyContent="flexEnd">
                            <Actions>
                                <Button onClick={onClose} variant="transparent">
                                    Cancel
                                </Button>
                                <Button onClick={handleSubmit}>Update</Button>
                            </Actions>
                        </Box>
                    </Stack>
                </Dialog>
            )}
        </Box>
    );
};

export default SendChequesToBankDialog;
